import { useState } from 'react';

import { PropTypes } from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import AccordionDetails from '@zenbusiness/zui/AccordionDetails';
import AccordionSummary from '@zenbusiness/zui/AccordionSummary';
import Box from '@zenbusiness/zui/Box';
import Chip from '@zenbusiness/zui/Chip';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_EVENTS } from '#client/constants/clickstream/failedPaymentRecovery';

import {
  InvoiceCardRoot,
  InvoiceCardAccordion,
  InvoiceCardHeader,
  InvoiceCardTitleRow,
  InvoiceCardAvatar,
  InvoiceCardAmountColumn,
  InvoiceCardTitle,
  InvoiceCardDueDate,
  InvoiceCardAmount,
  InvoiceCardDetailsSurface,
  InvoiceCardDetailsContent,
  InvoiceCardDivider,
  InvoiceCardLockIcon,
  InvoiceCardLockedMessage,
  InvoiceCardActions,
  InvoiceCardPrimaryButton,
  InvoiceCardPaymentLogo,
  InvoiceCardSecondaryButton
} from './InvoiceCard.style';
import { PAYMENT_LOGO_URL } from '../../constants';

const MESSAGE_IDS = {
  title: 'dashboard.invoicePayments.invoiceCard.title',
  dueDate: 'dashboard.invoicePayments.invoiceCard.dueDate',
  details: 'dashboard.invoicePayments.invoiceCard.details',
  unpaid: 'dashboard.invoicePayments.invoiceCard.unpaid',
  pay: 'dashboard.invoicePayments.invoiceCard.pay',
  payWithAnotherMethod: 'dashboard.invoicePayments.invoiceCard.payWithAnotherMethod',
  payPreviousFirst: 'dashboard.invoicePayments.invoiceCard.payPreviousFirst',
  payLogoAlt: 'dashboard.invoicePayments.invoiceCard.payLogoAlt'
};

function InvoiceCard({
  amount,
  avatarUrl,
  dueDate,
  dynamicProperties,
  isLocked = false,
  lineItems = [],
  onPay,
  onPayWithAnotherMethod
}) {
  const { formatMessage: fm, formatNumber } = useIntl();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  return (
    <InvoiceCardRoot isLocked={isLocked}>
      <InvoiceCardHeader>
        <InvoiceCardTitleRow>
          <Box>
            <InvoiceCardTitle>
              <FormattedMessage id={MESSAGE_IDS.title} />
            </InvoiceCardTitle>
            <InvoiceCardDueDate>
              <FormattedMessage id={MESSAGE_IDS.dueDate} values={{ dueDate }} />
            </InvoiceCardDueDate>
          </Box>
          {!isLocked && avatarUrl && <InvoiceCardAvatar src={avatarUrl} />}
        </InvoiceCardTitleRow>
        <InvoiceCardAmountColumn>
          <InvoiceCardAmount>
            {formatNumber(amount, { style: 'currency', currency: 'USD' })}
          </InvoiceCardAmount>
          <Chip color="error" label={<FormattedMessage id={MESSAGE_IDS.unpaid} />} size="sm" />
        </InvoiceCardAmountColumn>
      </InvoiceCardHeader>

      <InvoiceCardAccordion
        clickstreamProps={{ elementName: 'invoice_details_accordion' }}
        disableGutters
        expanded={isLocked ? false : detailsExpanded}
        isLocked={isLocked}
        onChange={(event, expanded) => {
          if (!isLocked) {
            setDetailsExpanded(expanded);
          }
        }}
      >
        <AccordionSummary aria-disabled={isLocked}>
          <FormattedMessage id={MESSAGE_IDS.details} />
        </AccordionSummary>
        <AccordionDetails>
          <InvoiceCardDetailsSurface>
            <InvoiceCardDetailsContent>
              <Stack spacing={1.5}>
                {lineItems.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={`${index}-${item.description}`} display="flex" justifyContent="space-between">
                    <Text size="sm">{item.description}</Text>
                    <Text size="sm" weight="medium">
                      {formatNumber(parseFloat(item.amount) || 0, { style: 'currency', currency: 'USD' })}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </InvoiceCardDetailsContent>
          </InvoiceCardDetailsSurface>
        </AccordionDetails>
      </InvoiceCardAccordion>

      {isLocked ? (
        <>
          <InvoiceCardDivider />
          <InvoiceCardLockedMessage>
            <InvoiceCardLockIcon aria-hidden />
            <FormattedMessage id={MESSAGE_IDS.payPreviousFirst} />
          </InvoiceCardLockedMessage>
        </>
      ) : (
        <InvoiceCardActions>
          <InvoiceCardPrimaryButton
            clickstreamProps={{ ...CLICKSTREAM_EVENTS.payWithKlarna, dynamicProperties }}
            onClick={onPay}
          >
            <Stack alignItems="center" direction="row" justifyContent="center" spacing={1}>
              <FormattedMessage id={MESSAGE_IDS.pay} />
              <InvoiceCardPaymentLogo
                alt={fm({ id: MESSAGE_IDS.payLogoAlt })}
                src={PAYMENT_LOGO_URL}
              />
            </Stack>
          </InvoiceCardPrimaryButton>
          <InvoiceCardSecondaryButton
            clickstreamProps={{ ...CLICKSTREAM_EVENTS.payWithRecurly, dynamicProperties }}
            onClick={onPayWithAnotherMethod}
          >
            <FormattedMessage id={MESSAGE_IDS.payWithAnotherMethod} />
          </InvoiceCardSecondaryButton>
        </InvoiceCardActions>
      )}
    </InvoiceCardRoot>
  );
}

InvoiceCard.propTypes = {
  amount: PropTypes.number.isRequired,
  avatarUrl: PropTypes.string,
  dueDate: PropTypes.string.isRequired,
  dynamicProperties: PropTypes.shape({
    totalUnpaidInvoiceAmount: PropTypes.number,
    totalUnpaidInvoiceCount: PropTypes.number,
    currentUnpaidInvoiceApiId: PropTypes.string,
    currentUnpaidInvoiceAmount: PropTypes.number,
    unpaidInvoiceApiIds: PropTypes.arrayOf(PropTypes.string),
    hadSuccessPayment: PropTypes.bool,
    experimentName: PropTypes.string,
    experimentTreatment: PropTypes.string,
    currentPaidInvoiceApiId: PropTypes.string,
    currentPaidInvoiceAmount: PropTypes.number,
    errorMessage: PropTypes.string
  }),
  isLocked: PropTypes.bool,
  lineItems: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.string,
    amount: PropTypes.string
  })),
  onPay: PropTypes.func,
  onPayWithAnotherMethod: PropTypes.func
};

export default InvoiceCard;
